/**
 * nanuParser.ts
 * .nanu バイナリをブラウザ上でパースして ParsedInoData を返す。
 *
 * .nanu フォーマット:
 *   [4]  Magic: "NANU"
 *   [1]  Version: 1
 *   [2]  JP text count (uint16 LE)
 *   [2]  EN text count (uint16 LE)
 *   [2]  Tile count    (uint16 LE)
 *   [2]  Map count     (uint16 LE)
 *   [2]  Music count   (uint16 LE)
 *   [4]  API URL length    (uint32 LE)
 *   [4]  API Token length  (uint32 LE)
 *   --- variable data ---
 *   JP texts:  [uint32 LE length][UTF-8 bytes] × jpTextCount
 *   EN texts:  [uint32 LE length][UTF-8 bytes] × enTextCount
 *   Tiles:     [uint32 LE length][bytes] × tileCount
 *   Maps:      [uint32 LE length][bytes] × mapCount
 *   Music:     [uint32 LE length][bytes] × musicCount
 *   API URL bytes
 *   API Token bytes
 */
import type { ParsedInoData } from "./inoParser";

const MAGIC = "NANU";
const SUPPORTED_VERSION = 1;
const HEADER_SIZE = 23;
const MAX_DAYDREAM_TOTAL_SIZE = 16 * 1024 * 1024;
const MAX_COLLECTION_COUNT = 4096;
const MAX_TOTAL_ENTRY_COUNT = 8192;
const MAX_ENTRY_LENGTH = 4 * 1024 * 1024;

export function parseNanuFile(buffer: ArrayBuffer): ParsedInoData {
    if (buffer.byteLength < HEADER_SIZE) {
        throw new Error(
            `Invalid .nanu file: header requires ${HEADER_SIZE} bytes (got ${buffer.byteLength})`,
        );
    }
    if (buffer.byteLength > MAX_DAYDREAM_TOTAL_SIZE) {
        throw new Error(
            `Invalid .nanu file: total size exceeds the Daydream limit (${MAX_DAYDREAM_TOTAL_SIZE} bytes)`,
        );
    }

    const view = new DataView(buffer);
    const bytes = new Uint8Array(buffer);
    const dec = new TextDecoder("utf-8");

    // マジック確認
    const magic = String.fromCharCode(bytes[0], bytes[1], bytes[2], bytes[3]);
    if (magic !== MAGIC) {
        throw new Error(`Invalid .nanu file (magic mismatch: got "${magic}")`);
    }

    const version = bytes[4];
    if (version !== SUPPORTED_VERSION) {
        throw new Error(`Unsupported .nanu version: ${version}`);
    }

    let off = 5;

    const jpTextCount = view.getUint16(off, true);
    off += 2;
    const enTextCount = view.getUint16(off, true);
    off += 2;
    const tileCount = view.getUint16(off, true);
    off += 2;
    const mapCount = view.getUint16(off, true);
    off += 2;
    const musicCount = view.getUint16(off, true);
    off += 2;
    const apiUrlLen = view.getUint32(off, true);
    off += 4;
    const apiTokenLen = view.getUint32(off, true);
    off += 4;

    const collections = [
        ["Japanese text", jpTextCount],
        ["English text", enTextCount],
        ["tile", tileCount],
        ["map", mapCount],
        ["music", musicCount],
    ] as const;
    for (const [label, count] of collections) {
        if (count > MAX_COLLECTION_COUNT) {
            throw new Error(
                `Invalid .nanu ${label} count: ${count} exceeds the Daydream limit (${MAX_COLLECTION_COUNT})`,
            );
        }
    }

    const totalEntryCount = collections.reduce(
        (total, [, count]) => total + count,
        0,
    );
    if (totalEntryCount > MAX_TOTAL_ENTRY_COUNT) {
        throw new Error(
            `Invalid .nanu total entry count: ${totalEntryCount} exceeds the Daydream limit (${MAX_TOTAL_ENTRY_COUNT})`,
        );
    }
    if (apiUrlLen > MAX_ENTRY_LENGTH) {
        throw new Error(
            `Invalid .nanu API URL length: ${apiUrlLen} exceeds the Daydream entry limit (${MAX_ENTRY_LENGTH} bytes)`,
        );
    }
    if (apiTokenLen > MAX_ENTRY_LENGTH) {
        throw new Error(
            `Invalid .nanu API token length: ${apiTokenLen} exceeds the Daydream entry limit (${MAX_ENTRY_LENGTH} bytes)`,
        );
    }

    const remainingAfterHeader = bytes.length - off;
    const apiLength = apiUrlLen + apiTokenLen;
    if (apiLength > remainingAfterHeader) {
        throw new Error(
            "Invalid .nanu API URL/token lengths: fields extend past the input",
        );
    }
    const minimumVariableLength = totalEntryCount * 4 + apiLength;
    if (minimumVariableLength > remainingAfterHeader) {
        throw new Error(
            "Invalid .nanu counts: length prefixes and API fields extend past the input",
        );
    }

    function ensureAvailable(length: number, label: string): void {
        if (!Number.isSafeInteger(length) || length < 0) {
            throw new Error(`Invalid .nanu ${label}: invalid length ${length}`);
        }
        if (length > bytes.length - off) {
            throw new Error(
                `Invalid .nanu ${label}: ${length} bytes extend past the input at offset ${off}`,
            );
        }
    }

    // 長さプレフィックス付きバイト列を読む
    function readLenPrefixedBytes(label: string): Uint8Array {
        ensureAvailable(4, `${label} entry length`);
        const len = view.getUint32(off, true);
        off += 4;
        if (len > MAX_ENTRY_LENGTH) {
            throw new Error(
                `Invalid .nanu ${label} entry length: ${len} exceeds the Daydream limit (${MAX_ENTRY_LENGTH} bytes)`,
            );
        }
        ensureAvailable(len, `${label} entry length`);
        const slice = bytes.slice(off, off + len);
        off += len;
        return slice;
    }

    function readLenPrefixedString(label: string): string {
        const b = readLenPrefixedBytes(label);
        return dec.decode(b);
    }

    function readLenPrefixedArray(count: number, label: string): Uint8Array[] {
        const result: Uint8Array[] = [];
        for (let i = 0; i < count; i++) {
            result.push(readLenPrefixedBytes(`${label} ${i}`));
        }
        return result;
    }

    function readLenPrefixedStrings(count: number, label: string): string[] {
        const result: string[] = [];
        for (let i = 0; i < count; i++) {
            result.push(readLenPrefixedString(`${label} ${i}`));
        }
        return result;
    }

    const textsJp = readLenPrefixedStrings(jpTextCount, "Japanese text");
    const textsEn = readLenPrefixedStrings(enTextCount, "English text");
    const tiles = readLenPrefixedArray(tileCount, "tile");
    const maps = readLenPrefixedArray(mapCount, "map");
    const music = readLenPrefixedArray(musicCount, "music");

    ensureAvailable(apiUrlLen, "API URL");
    const apiUrl = apiUrlLen > 0 ? dec.decode(bytes.slice(off, off + apiUrlLen)) : null; // prettier-ignore
    off += apiUrlLen;
    ensureAvailable(apiTokenLen, "API token");
    const apiToken = apiTokenLen > 0 ? dec.decode(bytes.slice(off, off + apiTokenLen)) : null; // prettier-ignore

    // texts は JP 優先 (JP がなければ EN)
    const maxLen = Math.max(textsJp.length, textsEn.length);
    const texts: string[] = [];
    for (let i = 0; i < maxLen; i++) {
        texts.push(textsJp[i] || textsEn[i] || "");
    }

    return {
        texts,
        textsJp,
        textsEn,
        apiUrl,
        apiToken,
        filesDetected: ["main.ino.nanu"],
        tiles,
        maps,
        music,
    };
}
